<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatPrice, type Product, type FeaturedProduct } from '../../api/catalog';
import { useCartStore } from '../../stores/cart';

const props = defineProps<{ product: Product | FeaturedProduct }>();

const { t } = useI18n();
const cart = useCartStore();
const snake = (s: string) => s.toUpperCase().replace(/ /g, '_');
const inCart = computed(() => cart.has(props.product.id));

const cover = computed(() => props.product.coverUrl || props.product.imageUrl || '');
const platformLabel = computed(() => (props.product.platform || 'GLOBAL').toUpperCase());
</script>

<template>
  <article class="group relative flex h-full flex-col border border-term-green bg-term-card">
    <!-- Cover -->
    <router-link
      :to="`/product/${product.id}`"
      class="relative block h-[280px] shrink-0 overflow-hidden border-b border-term-green bg-[#242c26]"
    >
      <img
        v-if="cover"
        :src="cover"
        :alt="product.name"
        loading="lazy"
        class="h-full w-full object-cover opacity-70 grayscale transition duration-300 group-hover:opacity-100 group-hover:grayscale-0"
      />
      <span class="absolute left-2 top-2 border border-term-green bg-term-bg px-[9px] py-[5px] text-[12px] uppercase leading-none text-term-green">
        [ {{ product.isDlc ? t('home.dlc') : t('home.baseGame') }} ]
      </span>
    </router-link>

    <!-- Body -->
    <div class="flex flex-1 flex-col gap-4 p-4">
      <router-link
        :to="`/product/${product.id}`"
        class="line-clamp-2 text-[16px] font-bold uppercase leading-[20px] text-term-ink transition-colors group-hover:text-term-green"
      >
        {{ product.name }}
      </router-link>
      <div class="mt-auto flex flex-col gap-[3px]">
        <p class="text-[12px] uppercase text-term-muted">{{ platformLabel }} // GLOBAL</p>
        <p class="text-[18px] leading-[28px] text-term-green">{{ formatPrice(product.ourPriceCents) }}</p>
      </div>
    </div>

    <!-- Add to cart -->
    <button
      type="button"
      :disabled="inCart || cart.isFull"
      class="border-t border-term-green py-[9px] text-center text-[14px] uppercase transition-colors disabled:cursor-default"
      :class="inCart ? 'bg-term-green/10 text-term-green' : 'text-term-green hover:bg-term-green/10 disabled:opacity-40'"
      @click="cart.add(product)"
    >
      [ {{ inCart ? snake(t('product.inCart')) : snake(t('home.addToCart')) }} ]
    </button>
  </article>
</template>
